// NeoenergiaCaptura.js
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './NeoenergiaCaptura.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import { UserContext } from '../../UserContext';

const chamarRoboOuFalhar = (protoUrl, onErro) => {
    try {
        window.location.href = protoUrl;
    } catch {
        onErro();
    }
};

// =============== utils tipo usina & debug ===============
const stripAccents = (s = '') =>
    s?.normalize?.('NFD').replace(/[\u0300-\u036f]/g, '') ?? String(s ?? '');
const asUpper = (s) => stripAccents(String(s || '')).trim().toUpperCase();

const deepHasConsorcio = (obj, depth = 0) => {
    if (!obj || depth > 4) return false;
    if (typeof obj === 'string') return asUpper(obj).includes('CONSORC');
    if (Array.isArray(obj)) return obj.some((x) => deepHasConsorcio(x, depth + 1));
    if (typeof obj === 'object') return Object.values(obj).some((v) => deepHasConsorcio(v, depth + 1));
    return false;
};

const isUsinaConsorcio = (u) => {
    if (!u) return false;
    if (u.ehConsorcio === true) return true;
    if (u?.tipoUsina?.ehConsorcio === true) return true;

    const nums = [
        u.idTipoUsina,
        u.tipoUsinaId,
        u?.tipoUsina?.id,
        u?.tipoUsina?.codigo,
        u?.tipoUsina?.codigoTipoUsina,
        u?.tipo_codigo,
        u?.tipo,
        u?.tipoUsina,
    ].map((v) => (v != null && v !== '' ? Number(v) : NaN));
    if (nums.some((n) => Number.isFinite(n) && n === 2)) return true;

    const texts = [
        u?.tipoUsina?.nome,
        u?.tipoUsina?.descricao,
        u?.tipoUsina?.sigla,
        u?.tipoUsina,
        u?.tipo,
        u?.nomeTipoUsina,
        u?.dsTipoUsina,
        u?.tipo_nome,
        u?.tipo_descricao,
        u?.categoria,
    ].map(asUpper).filter(Boolean);

    if (texts.some((t) => t === 'CONSORCIO' || t === 'CONSORCIO_COMPARTILHADO')) return true;
    if (texts.some((t) => t.includes('CONSORC'))) return true;

    if (deepHasConsorcio(u)) return true;
    return false;
};

const debugUsina = (u, isConsorcio) => {
    try {
        console.groupCollapsed('%c[HelloSol][NeoenergiaCaptura] Diagnostico da usina selecionada', 'color:#ff7a00;font-weight:bold;');
        console.debug('Usina selecionada:', u);
        console.debug('-> isConsorcio:', isConsorcio);
    } catch {}
    try { console.groupEnd(); } catch {}
};

// =============== helpers gerais ===============
const ERR_RE =
    /(erro|falha|exce[cç][aã]o|timeout|downloadexception|credenciais.*incompletas|nao foi poss[ií]vel|não foi possível)/i;


// Desembrulha sessionId tanto de {sessionId} quanto de {data:{sessionId}}
const resolveSessionId = (r) =>
    r?.sessionId ?? r?.data?.sessionId ?? r?.dados?.sessionId ?? r?.payload?.sessionId ?? null;

const resolveJobId = (r) =>
    r?.jobId ?? r?.data?.jobId ?? r?.dados?.jobId ?? r?.payload?.jobId ?? null;

const resolveTokenExecucao = (r) =>
    r?.tokenExecucao ?? r?.data?.tokenExecucao ?? r?.dados?.tokenExecucao ?? r?.payload?.tokenExecucao ?? null;

const resolveVersaoMinimaAgente = (r) =>
    r?.versaoMinimaAgente ?? r?.data?.versaoMinimaAgente ?? r?.dados?.versaoMinimaAgente ?? r?.payload?.versaoMinimaAgente ?? null;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatarNomeUsina = (nome) => (nome || 'a usina selecionada').trim();

const parseResultadoCaptura = (resultadoJson) => {
    if (!resultadoJson) return null;
    try {
        const parsed = typeof resultadoJson === 'string' ? JSON.parse(resultadoJson) : resultadoJson;
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
};

const getAnoMesAtual = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${now.getFullYear()}-${month}`;
};

const normalizarCompetenciaResultado = (competencia) => {
    const texto = String(competencia || '').trim();
    if (!texto) return '';
    if (/^\d{4}-\d{2}$/.test(texto)) return texto;
    if (/^\d{2}\/\d{4}$/.test(texto)) {
        const [mes, ano] = texto.split('/');
        return `${ano}-${mes}`;
    }
    return '';
};

const humanizarMensagemErro = (mensagem = '') => {
    const texto = String(mensagem || '').trim();
    const textoLower = texto.toLowerCase();

    if (!texto) {
        return 'Nao foi possivel concluir a captura.';
    }

    if (textoLower.includes('timeout aguardando login autenticado')) {
        return 'Nao foi possivel confirmar o login no portal da Neoenergia dentro do tempo esperado.';
    }

    if (textoLower.includes('credenciais') || textoLower.includes('login')) {
        return 'Nao foi possivel entrar no portal da Neoenergia com os dados informados.';
    }

    if (textoLower.includes('neoenergia nao encontrado') || textoLower.includes('robo neoenergia nao encontrado')) {
        return 'O assistente local do Hello Sol nao foi encontrado neste computador.';
    }

    if (textoLower.includes('portal da neoenergia')) {
        return texto;
    }

    if (textoLower.startsWith('erro fatal no fluxo do robo neoenergia:')) {
        return texto.replace(/^erro fatal no fluxo do robo neoenergia:\s*/i, '');
    }

    return texto;
};

const criarMensagemStatusCaptura = ({ statusJob, mensagemErro, nomeUsina }) => {
    const usina = formatarNomeUsina(nomeUsina);

    switch (statusJob) {
        case 'PENDENTE':
            return `Estamos preparando a captura das faturas da usina ${usina}.`;
        case 'EM_EXECUCAO':
            return `A captura das faturas da usina ${usina} esta em andamento neste computador.`;
        case 'SUCESSO':
            return `A captura das faturas da usina ${usina} foi concluida com sucesso.`;
        case 'SUCESSO_PARCIAL':
            return `A captura das faturas da usina ${usina} foi concluida, mas alguns itens precisam de revisao.`;
        case 'EXPIRADO':
            return `O tempo da captura das faturas da usina ${usina} expirou antes da conclusao.`;
        case 'CANCELADO':
            return `A captura das faturas da usina ${usina} foi cancelada.`;
        case 'ERRO':
            return `Nao foi possivel concluir a captura das faturas da usina ${usina}. ${humanizarMensagemErro(mensagemErro)}`;
        default:
            return `Estamos acompanhando a captura das faturas da usina ${usina}.`;
    }
};


const criarMensagensIniciaisCaptura = (nomeUsina) => {
    const usina = formatarNomeUsina(nomeUsina);
    return [
        { msg: `Iniciamos a preparacao da captura das faturas  da usina ${usina}.` },
        { msg: 'Abrimos o assistente local neste computador para acessar o portal da Neoenergia.' },
    ];
};

const ResumoResultadoCaptura = ({ resultado, onVerStatus, onVerConsumos }) => {
    if (!resultado) return null;

    const cards = [
        { label: 'Faturas analisadas', value: resultado.totalPendencias ?? resultado.resultadosPorUc?.length ?? 0 },
        { label: 'Importadas', value: resultado.totalSucesso ?? 0, tone: 'success' },
        { label: 'Com revisao', value: resultado.totalSucessoParcial ?? 0, tone: 'warning' },
        { label: 'Falhas', value: (resultado.totalErroDownload ?? 0) + (resultado.totalErroProcessamento ?? 0), tone: 'error' },
        { label: 'Clientes criados', value: resultado.totalClientesAutoCriados ?? 0 },
        { label: 'UCs criadas', value: resultado.totalUcAutoCadastradas ?? 0 },
        { label: 'Rateios criados', value: resultado.totalRateiosAutoCriados ?? 0 },
        { label: 'Sem fatura', value: resultado.totalSemFatura ?? 0 },
    ];

    const houveAutoCadastro =
        (resultado.totalClientesAutoCriados ?? 0) > 0 ||
        (resultado.totalUcAutoCadastradas ?? 0) > 0 ||
        (resultado.totalRateiosAutoCriados ?? 0) > 0;

    return (
        <div className={styles.summaryContainer}>
            <div className={styles.summaryHeader}>
                <h3>Resumo da importacao</h3>
                <p>Resultado operacional do lote processado pelo robo neste computador.</p>
            </div>

            <div className={styles.summaryGrid}>
                {cards.map((card) => (
                    <div
                        key={card.label}
                        className={`${styles.summaryCard} ${card.tone ? styles[`summaryCard${card.tone.charAt(0).toUpperCase()}${card.tone.slice(1)}`] : ''}`}
                    >
                        <span className={styles.summaryLabel}>{card.label}</span>
                        <strong className={styles.summaryValue}>{card.value}</strong>
                    </div>
                ))}
            </div>

            {houveAutoCadastro && (
                <div className={styles.summaryNotice}>
                    O robo criou cadastros operacionais automaticamente para nao interromper a importacao. Vale a pena revisar esses dados antes do fechamento do ciclo.
                </div>
            )}

            <div className={styles.summaryActions}>
                <button type="button" className={styles.botaoNeutro} onClick={onVerStatus}>
                    Ver status da importacao
                </button>
                <button type="button" className={styles.botaoNeutro} onClick={onVerConsumos}>
                    Consultar consumos gerados
                </button>
            </div>
        </div>
    );
};

// =============== subcomponentes ===============
const LogAtividades = ({ status, fase, hasError, nomeUsina }) => {
    const logRef = useRef(null);

    useEffect(() => {
        if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    }, [status]);

    const last = status.length ? status[status.length - 1] : null;
    const progress =
        last && typeof last.progress === 'number'
            ? last.progress
            : fase === 'concluido'
                ? 100
                : null;

    const titulo =
        fase === 'concluido'
            ? hasError
                ? 'Captura finalizada com pendencias'
                : 'Captura concluida com sucesso'
            : 'Captura em andamento';

    const subtitulo =
        fase === 'concluido'
            ? hasError
                ? `Verifique abaixo o que aconteceu com a usina ${formatarNomeUsina(nomeUsina)}.`
                : `Concluimos a captura das faturas da usina ${formatarNomeUsina(nomeUsina)}.`
            : `Estamos acompanhando a captura das faturas da usina ${formatarNomeUsina(nomeUsina)}.`;

    return (
        <div className={styles.logContainer}>
            <div
                className={`${styles.alert} ${
                    fase === 'concluido'
                        ? hasError
                            ? styles.alertError
                            : styles.alertSuccess
                        : styles.alertInfo
                }`}
            >
                <strong>{titulo}</strong>
                <span className={styles.alertMsg}>{subtitulo}</span>
            </div>

            {progress !== null && (
                <div className={styles.progressBarContainer}>
                    <div className={styles.progressBarFill} style={{ width: `${progress}%` }} />
                    <span className={styles.progressBarText}>{progress}%</span>
                </div>
            )}

            <div className={styles.statusBox} ref={logRef}>
                <div className={styles.statusTitle}>Acompanhamento da captura</div>
                <div className={styles.statusList}>
                    {status.length === 0 ? (
                        <div className={styles.statusItem}>Aguardando o inicio da captura...</div>
                    ) : (
                        status.map((s, i) => {
                            const msg = s?.msg ?? (typeof s === 'string' ? s : JSON.stringify(s));
                            const isErr = Boolean(s?.error) || ERR_RE.test(msg || '');
                            return (
                                <div key={i} className={`${styles.statusItem} ${isErr ? styles.statusError : ''}`}>
                                    - {msg}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

// =============== tela principal ===============
function NeoenergiaCaptura() {
    const { user } = useContext(UserContext);
    const idEmpresa = user?.empresa?.id;
    const navigate = useNavigate();

    const { get, post } = useHttpClient();
    const { exibirErro } = useNotificacoes(); // apenas para validacoes locais

    const [usinas, setUsinas] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [form, setForm] = useState({ idUsina: '', idCliente: '' });

    const [fase, setFase] = useState('selecao');
    const [status, setStatus] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resultadoFinal, setResultadoFinal] = useState(null);
    const closingRef = useRef(false);

    // buscar usinas
    useEffect(() => {
        if (!idEmpresa) return;
        (async () => {
            try {
                const data = await get(`/hello-sol/usinas/buscar?idEmpresa=${idEmpresa}`);
                setUsinas(Array.isArray(data) ? data : []);
            } catch {
                setUsinas([]);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idEmpresa]);

    // consórcio?
    const usinaSelecionada = useMemo(
        () => usinas.find((u) => String(u.id) === String(form.idUsina)),
        [usinas, form.idUsina]
    );
    const isConsorcio = useMemo(() => isUsinaConsorcio(usinaSelecionada), [usinaSelecionada]);

    // DEBUG a cada troca de usina
    useEffect(() => {
        if (form.idUsina && usinaSelecionada) debugUsina(usinaSelecionada, isConsorcio);
    }, [form.idUsina, usinaSelecionada, isConsorcio]);

    // carregar clientes se for consórcio
    useEffect(() => {
        if (!form.idUsina || !isConsorcio) {
            setClientes([]);
            return;
        }
        (async () => {
            try {
                const qs = new URLSearchParams({ idUsina: form.idUsina, idTipoUsuario: 3 }).toString();
                const resp = await get(`/hello-sol/usuarios/buscarUsuarioUsina?${qs}`);
                setClientes(Array.isArray(resp) ? resp : (resp?.content ?? []));
            } catch {
                setClientes([]);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.idUsina, isConsorcio]);

    // se mudou para não-consórcio, limpa cliente
    useEffect(() => {
        if (!isConsorcio && form.idCliente) {
            setForm((p) => ({ ...p, idCliente: '' }));
        }
    }, [isConsorcio, form.idCliente]);

    const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    // ---------- RESET p/ tela inicial ----------
    const resetToSelecao = async () => {
        if (closingRef.current) return;
          closingRef.current = true;
        setStatus([]);
        setHasError(false);
        setLoading(false);
        setResultadoFinal(null);
        setFase('selecao');
        closingRef.current = false;
    };
    // ---------- sessão ----------
    const fecharSessao = async () => {
        await resetToSelecao();
    };

    // ---------- iniciar fluxo ----------
    const { exibirErro: toastErro } = useNotificacoes(); // só para validação local

    const baixarLauncher = async () => {
        try {
            setLoading(true);
            const blob = await get('/hello-sol/robos-neoenergiadf/launcher/download', {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'hello-sol-robo-neoenergia.exe';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            exibirErro('Nao foi possivel baixar o executavel do robo.');
        } finally {
            setLoading(false);
        }
    };

    const monitorarJob = async (jobId) => {
        const finais = ['SUCESSO', 'SUCESSO_PARCIAL', 'ERRO', 'EXPIRADO', 'CANCELADO'];
        const inicio = Date.now();
        const timeoutMs = 30 * 60 * 1000;
        const nomeUsina = usinaSelecionada?.nome;

        while (Date.now() - inicio < timeoutMs) {
            const job = await get(`/hello-sol/robos-neoenergiadf/jobs/${jobId}`);
            const statusJob = job?.status || 'PENDENTE';
            const msg = criarMensagemStatusCaptura({
                statusJob,
                mensagemErro: job?.mensagemErro,
                nomeUsina,
            });

            setStatus((prev) => {
                const anterior = prev[prev.length - 1]?.msg;
                return anterior === msg ? prev : [...prev, { msg, error: statusJob === 'ERRO' }];
            });

            if (finais.includes(statusJob)) {
                setResultadoFinal(parseResultadoCaptura(job?.resultadoJson));
                setHasError(['ERRO', 'EXPIRADO', 'CANCELADO'].includes(statusJob));
                setFase('concluido');
                setLoading(false);
                return;
            }

            await sleep(5000);
        }

        setHasError(true);
        setFase('concluido');
        setLoading(false);
        setStatus((prev) => [
            ...prev,
            {
                msg: `A captura das faturas da usina ${formatarNomeUsina(usinaSelecionada?.nome)} demorou mais do que o esperado para ser finalizada.`,
                error: true,
            },
        ]);
    };

    const abrirStatusImportacao = () => {
        const competencia =
            normalizarCompetenciaResultado(resultadoFinal?.resultadosPorUc?.[0]?.competencia) ||
            getAnoMesAtual();
        navigate(`/home/listar-status-faturas?idUsina=${encodeURIComponent(form.idUsina)}&competencia=${encodeURIComponent(competencia)}`);
    };

    const abrirConsumosGerados = () => {
        navigate('/home/listar-cliente-consumo');
    };

    const iniciarLogin = async (e) => {
        e.preventDefault();

        if (!form.idUsina) return toastErro('Selecione a usina.');
        if (isConsorcio && !form.idCliente)
            return toastErro('Selecione o cliente (usina do tipo Consórcio).');

        setLoading(true);
        setStatus([]);
        setHasError(false);
        setFase('capturando');

        try {
            // 1) START - backend cria job e valida credenciais
            const startResp = await post(
                '/hello-sol/robos-neoenergiadf/session/start',
                {
                    idUsina: form.idUsina,
                    idCliente: isConsorcio ? form.idCliente : null,
                },
                {}
            );


            const sid = resolveSessionId(startResp);
            if (!sid) throw new Error('Falha ao iniciar sessao do robo (sessionId ausente).');
            const jobId = resolveJobId(startResp);
            const tokenExecucao = resolveTokenExecucao(startResp);
            const versaoMinimaAgente = resolveVersaoMinimaAgente(startResp);
            if (!jobId || !tokenExecucao) throw new Error('Falha ao iniciar job do robo.');
            const backendApiRoot = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, '');
            const backendBaseUrl = `${backendApiRoot}/hello-sol/robos-neoenergiadf`;

            // 2) DISPARA O .EXE (worker local com fallback HTTP)
            const protoUrl =
                `hellosolrobo://start` +
                `?sessionId=${encodeURIComponent(sid)}` +
                `&jobId=${encodeURIComponent(jobId)}` +
                `&tokenExecucao=${encodeURIComponent(tokenExecucao)}` +
                `&minAgentVersion=${encodeURIComponent(versaoMinimaAgente || '')}` +
                `&backendBaseUrl=${encodeURIComponent(backendBaseUrl)}` +
                `&idUsina=${encodeURIComponent(form.idUsina)}` +
                `&idCliente=${encodeURIComponent(isConsorcio ? form.idCliente : '')}`;

            setStatus(criarMensagensIniciaisCaptura(usinaSelecionada?.nome));

            chamarRoboOuFalhar(protoUrl, () => {
                exibirErro(
                    'Robo Neoenergia nao encontrado. ' +
                    'Faca o download e instale antes de continuar.'
                );
            });

            await monitorarJob(jobId);
            return;
        } catch (err) {
            await resetToSelecao();
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                'Nao foi possivel iniciar a captura.';
            exibirErro(msg);
        }
    };


    const clienteHabilitado = Boolean(form.idUsina) && isConsorcio;

    return (
        <div className={styles_global.container}>
            <h2>Capturar Fatura - Neoenergia DF</h2>

            {fase === 'selecao' && (
                <form className={styles.filterForm} onSubmit={iniciarLogin}>
                    <div className={styles_content.formField}>
                        <label>Usina</label>
                        <select
                            name="idUsina"
                            value={form.idUsina}
                            onChange={onChange}
                            required
                            className={styles.campoUsina}
                        >
                            <option value="">Selecione a Usina</option>
                            {usinas.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.nome}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label>Cliente</label>
                        <select
                            name="idCliente"
                            value={form.idCliente}
                            onChange={onChange}
                            disabled={!clienteHabilitado}
                            className={`${styles.campoCliente} ${!clienteHabilitado ? styles.inputDisabled : ''}`}
                            title={!clienteHabilitado ? 'Disponível apenas para usina do tipo Consórcio' : ''}
                            required={clienteHabilitado}
                        >
                            <option value="">
                                {clienteHabilitado ? 'Selecione' : 'Indisponível (Apenas Usina do Tipo Consórcio)'}
                            </option>
                            {clienteHabilitado &&
                                clientes.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.nome}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className={styles.botoesAbaixo}>
                        <button
                            type="button"
                            className={styles.botaoDownload}
                            onClick={baixarLauncher}
                            disabled={loading}
                        >
                            Baixar robo
                        </button>
                        <button type="submit" className={styles.botaoAcao} disabled={loading || !form.idUsina}>
                            {loading ? 'Iniciando...' : 'Iniciar Captura'}
                        </button>
                    </div>
                </form>
            )}

            {(fase === 'capturando' || fase === 'concluido') && (
                <>
                    {fase === 'concluido' && (
                        <ResumoResultadoCaptura
                            resultado={resultadoFinal}
                            onVerStatus={abrirStatusImportacao}
                            onVerConsumos={abrirConsumosGerados}
                        />
                    )}
                    <LogAtividades
                        status={status}
                        fase={fase}
                        hasError={hasError}
                        nomeUsina={usinaSelecionada?.nome}
                    />
                    <div className={styles.botoesAbaixo}>
                        <button
                            onClick={fecharSessao}
                            className={styles.botaoNeutro}
                            disabled={fase === 'capturando' && loading}
                        >
                            {fase === 'concluido' ? 'Finalizar e Voltar' : 'Cancelar Sessão'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default NeoenergiaCaptura;

